import { motion } from "framer-motion";
import { ArrowLeft, ScrollText, Image as ImageIcon, Copyright, Link2, ShieldAlert, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";

const CONTACT_EMAIL = "cphhighlights@gmail.com";
const UPDATED = "10 September 2026";

const SECTIONS = [
  {
    icon: ScrollText,
    title: "1. About this guide",
    body: [
      "CPH Highlights is an independent, unofficial travel guide to Copenhagen. It is not affiliated with, endorsed by, or connected to the City of Copenhagen, Visit Denmark, Wonderful Copenhagen, or any of the attractions, venues, transport operators or brands mentioned on the site.",
      "The content — area guides, opening hours, prices, transport tips and the day-planner itineraries — is provided for general information only. Details change often. Always confirm opening hours, prices, tickets, seasonal closures and cancellations with the official source before you travel or spend money.",
    ],
  },
  {
    icon: Copyright,
    title: "2. Copyright & intellectual property",
    body: [
      `All original text and written content on this site — every area guide, itinerary, description, recommendation and the underlying ideas, research and curation about Copenhagen, together with the layout, design, branding, the "CPH Highlights" name and the day-planner logic — is © ${new Date().getFullYear()} CPH Highlights and protected by copyright and other intellectual-property laws. All rights reserved.`,
      "You are welcome to view, share links to, and use the itineraries you build for your own personal, non-commercial travel. You may NOT copy, reproduce, rewrite, scrape, translate, republish, redistribute or otherwise reuse our text or our ideas about Copenhagen — in whole or in part — for any commercial purpose without our prior written permission.",
      "We actively monitor for unauthorised use and we will enforce our rights. Copying our text or ideas for commercial use without permission is a breach of these terms and of copyright law, and may result in takedown requests, DMCA notices, invoices for unauthorised use and legal action.",
    ],
  },
  {
    icon: ImageIcon,
    title: "3. Image rights & photography",
    body: [
      "Photographs and images on this site are either owned by CPH Highlights, licensed from stock photography providers (such as Unsplash and Pexels) under their respective licences, or supplied by partners and venues for use here. They remain the property of their original copyright holders.",
      "Images may not be downloaded, copied or reused from this site without permission from the relevant rights holder. Landmark names, logos and trademarks that appear in photos (for example Tivoli, Freetown Christiania, or transport operators) belong to their respective owners and are shown for identification and editorial purposes only.",
      `If you are the rights holder of an image used on this site and would like a credit added or the image removed, please contact us at ${CONTACT_EMAIL} and we'll respond promptly.`,
    ],
  },
  {
    icon: Link2,
    title: "4. External links & third-party services",
    body: [
      "The site links to third-party websites, maps and apps (for example Google Maps, DSB, Rejsebillet and the Copenhagen Card) for your convenience. We do not control these services and are not responsible for their content, availability, pricing, or privacy practices. Using them is subject to their own terms.",
      "Some placements on the site may be sponsored or paid advertising. Where that is the case it does not change our editorial recommendations.",
    ],
  },
  {
    icon: ShieldAlert,
    title: "5. No warranty & limitation of liability",
    body: [
      'The site and its content are provided "as is", without warranties of any kind. We do our best to keep information accurate and up to date, but we make no guarantees that it is complete, current or error-free.',
      "To the fullest extent permitted by law, CPH Highlights is not liable for any loss, cost or inconvenience arising from your use of the site or reliance on its information — including missed attractions, changed opening hours, ticket prices, transport disruptions, and venues, tours, events or activities (such as museums) that are closed, cancelled or sold out. You travel and plan at your own discretion.",
      "These terms may be updated from time to time; the current version always lives on this page. Where applicable, they are governed by Danish law.",
    ],
  },
];

export default function TermsPage() {
  return (
    <main data-testid="terms-page" className="pt-16 md:pt-20">
      <Seo
        title="Terms & Copyright | CPH Highlights"
        description="Terms of use for CPH Highlights, the unofficial Copenhagen travel guide — including copyright, image rights and photography credits, third-party links and liability."
        path="/terms"
        noindex
      />

      {/* header */}
      <section className="bg-[#13141A] text-white border-b border-zinc-200">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 mb-8 hover:bg-white hover:text-black transition-colors"
          >
            <ArrowLeft size={16} /> Back to start
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">TERMS &amp; COPYRIGHT</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            The small print,<br />
            <span className="font-editorial italic font-600">kept human</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6">
            How you can use CPH Highlights, who owns the words and images, and the usual disclaimers.
            Last updated {UPDATED}.
          </p>
        </div>
      </section>

      {/* body */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-24 space-y-6">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} y={30}>
                <div
                  data-testid={`terms-section-${s.title.split(".")[0]}`}
                  className="bg-white border border-zinc-200 shadow-brut p-6 md:p-9"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="shrink-0 w-10 h-10 grid place-items-center bg-[#13141A] text-white">
                      <Icon size={18} />
                    </span>
                    <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl">{s.title}</h2>
                  </div>
                  <div className="space-y-3">
                    {s.body.map((p, i) => (
                      <p key={i} className="font-body text-base text-black/70 leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}

          {/* contact */}
          <Reveal y={30}>
            <div className="bg-[#DF3226] text-white shadow-brut p-6 md:p-9 flex flex-col md:flex-row md:items-center gap-5 md:justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Mail size={18} />
                  <h2 className="font-display font-900 tracking-tight text-2xl">Questions or a rights request?</h2>
                </div>
                <p className="font-body text-sm text-white/85 max-w-md">
                  Copyright, image credits, corrections or anything else — get in touch and we’ll sort it.
                </p>
              </div>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=CPH%20Highlights%20—%20Terms%20enquiry`}
                data-testid="terms-contact-email"
                className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-base bg-black text-white px-6 py-4 rounded-full border border-white/20 hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
